// The ONE "Link Aircraft" (+) dialog. A record's Aircraft List links existing
// aircraft to it (Aaron 2026-10-06: inspection 2554 "link other aircraft same as
// config"; AD/SB 1577 "(+) icons to add rows (link to data)").
//
// Picks one or more aircraft (the shared multi picker; airframes already on the
// list are not offered) and, when the record's write takes one, the limitation
// (the shared LimitationFields — Based On and Period required, as legacy). The
// WRITE is the caller's: each record links through its own existing route and
// reports per-aircraft outcomes, which this dialog shows — a partial link names
// what got nothing and why.
//
// Callers: inspections-tr/dialogs/LinkAircraftDialog (the inspection's Aircraft
// List, both hosts) · ads-and-sbs/record/DirectiveLimitationsTab (the AD/SB
// record's Aircraft List, which takes no limitation: the row copies the model's).
import React, { useEffect, useState } from "react";
import { Grid, Typography } from "@mui/material";
import { useSnackbar } from "notistack";

import { AircraftMultiAutocomplete, type AircraftOption } from "./AircraftAutocomplete";
import LimitationFields, {
  BLANK_LIMITATION,
  missingInterval,
  type BasedOnChoice,
  type LimitationFieldValues,
} from "./LimitationFields";
import LinkDialogFrame, { type LinkOutcome } from "./LinkDialogFrame";

export interface LinkAircraftLimitation {
  /** Pre-fill, read when the dialog opens; a failed read leaves the fields blank. */
  loadDefaults: () => Promise<LimitationFieldValues>;
  basedOnOptions: BasedOnChoice[];
}

interface Props {
  open: boolean;
  /** Airframes already on the list — not offered. */
  linkedAircraftIds: number[];
  /** Omit when the write takes no limitation of its own. */
  limitation?: LinkAircraftLimitation;
  /** A line under the picker saying where the limitation comes from, when it is not asked for. */
  note?: React.ReactNode;
  /** `picked` carries each aircraft's registration, for naming a refusal. */
  submit: (
    aircraftIds: number[],
    limitation: LimitationFieldValues | null,
    picked: AircraftOption[]
  ) => Promise<LinkOutcome>;
  onClose: () => void;
  onLinked: (outcome: LinkOutcome) => void;
}

export default function LinkAircraftDialog({
  open,
  linkedAircraftIds,
  limitation,
  note,
  submit,
  onClose,
  onLinked,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [aircraft, setAircraft] = useState<AircraftOption[]>([]);
  const [form, setForm] = useState<LimitationFieldValues>(BLANK_LIMITATION);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const loadDefaults = limitation?.loadDefaults;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setAircraft([]);
    setError(null);
    setForm(BLANK_LIMITATION);
    loadDefaults?.()
      .then((d) => {
        if (!cancelled) setForm(d);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [open, loadDefaults]);

  const onSubmit = async () => {
    if (!aircraft.length) {
      setError("Choose at least one aircraft to link");
      return;
    }
    const missing = limitation ? missingInterval(form) : null;
    if (missing) {
      setError(missing);
      return;
    }
    setSaving(true);
    try {
      const outcome = await submit(
        aircraft.map((a) => a.id),
        limitation ? form : null,
        aircraft
      );
      if (outcome.created) {
        enqueueSnackbar(`${outcome.created} aircraft linked`, { variant: "success" });
      }
      if (outcome.skipped.length) {
        enqueueSnackbar(
          `Not linked: ${outcome.skipped.map((s) => `${s.label} (${s.reason})`).join("; ")}`,
          { variant: "warning" }
        );
      }
      onLinked(outcome);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Link failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <LinkDialogFrame open={open} title="Link Aircraft" error={error} saving={saving} onSubmit={onSubmit} onClose={onClose}>
      <Grid item xs={12}>
        <AircraftMultiAutocomplete
          value={aircraft}
          onChange={(next) => {
            setAircraft(next);
            setError(null);
          }}
          excludeIds={linkedAircraftIds}
        />
      </Grid>
      {note && (
        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary">
            {note}
          </Typography>
        </Grid>
      )}
      {limitation && (
        <LimitationFields value={form} onChange={setForm} basedOnOptions={limitation.basedOnOptions} required />
      )}
    </LinkDialogFrame>
  );
}
